/**
 * Seattle Metro 専用マップオーバーレイ
 *
 * 機能:
 * - #3: ダイス番号バッジ（各Town/City中央に表示）
 * - #5: 輸送済みキューブ数（左上オーバーレイ）
 * - #6: 需要過多警告（空き2以下のダイスグループに警告）
 */

import type { SeattleMapOverlayData } from '../../types/ui-interfaces';
import { axialToPixel } from './MapDisplay';
import { useTranslations } from '../../i18n';

interface SeattleMapOverlayProps {
  data: SeattleMapOverlayData;
  hexSize: number;
  centerOffsetX: number;
  centerOffsetY: number;
  rotationAngle: number;
}

/**
 * Seattle Metro マップオーバーレイ
 * SVG内で最前面に描画される
 */
export function SeattleMapOverlay({
  data,
  hexSize,
  centerOffsetX,
  centerOffsetY,
  rotationAngle,
}: SeattleMapOverlayProps) {
  return (
    <g className="seattle-overlay" style={{ pointerEvents: 'none' }}>
      {/* #3: ダイス番号バッジ（各ロケーション中央の白丸内） */}
      {data.diceBadges.map((badge) => {
        const { x, y } = axialToPixel(badge.coordinate.q, badge.coordinate.r, hexSize);
        const cx = x + centerOffsetX;
        const cy = y + centerOffsetY;
        // 白丸のサイズに合わせて配置（hexSize * 0.35 程度）
        const badgeRadius = hexSize * 0.38;

        return (
          <g
            key={`dice-badge-${badge.locationId}`}
            transform={`rotate(${-rotationAngle}, ${cx}, ${cy})`}
          >
            {/* 白丸背景 */}
            <circle
              cx={cx}
              cy={cy}
              r={badgeRadius}
              fill="white"
              stroke="none"
            />
            {/* ダイス番号 */}
            <text
              x={cx}
              y={cy + badgeRadius * 0.55}
              textAnchor="middle"
              className="fill-foreground font-bold"
              style={{ fontSize: `${badgeRadius * 1.6}px` }}
            >
              {badge.diceValue}
            </text>
          </g>
        );
      })}

      {/* #6: 需要過多警告（空き2以下のダイスグループ + Airport満杯） */}
      {data.warnings.map((warning) => {
        const { x, y } = axialToPixel(warning.centerCoordinate.q, warning.centerCoordinate.r, hexSize);
        const cx = x + centerOffsetX;
        const cy = y + centerOffsetY;

        // ダイス目ごとの外周方向オフセット
        // 1:左下, 2:左上, 3:下, 4:上, 5:右下, 6:右上, 0:Airport(右)
        const offsetDistance = hexSize * 2.5;
        let offsetX = 0;
        let offsetY = 0;
        let labelText = `Area ${warning.diceValue}`;

        switch (warning.diceValue) {
          case 1: // 左下
            offsetX = -offsetDistance;
            offsetY = -offsetDistance * 0.3;
            break;
          case 2: // 左上
            offsetX = offsetDistance * 1.2;
            offsetY = -offsetDistance * 0.3;
            break;
          case 3: // 下
            offsetX = -offsetDistance * 1.2;
            offsetY = offsetDistance * 0.7;
            break;
          case 4: // 上
            offsetX = offsetDistance * 1.3;
            offsetY = -offsetDistance;
            break;
          case 5: // 右下
            offsetX = -offsetDistance * 1.2;
            offsetY = offsetDistance * 0.7;
            break;
          case 6: // 右上
            offsetX = offsetDistance * 1.6;
            offsetY = offsetDistance * 0.3;
            break;
          case 0: // Airport (右方向)
            offsetX = 0;
            offsetY = 0;
            labelText = '';
            break;
        }

        const warningX = cx + offsetX;
        const warningY = cy + offsetY;

        return (
          <g
            key={`warning-${warning.diceValue}`}
            transform={`rotate(${-rotationAngle}, ${warningX}, ${warningY})`}
            className="warning-icon"
          >
            {/* 赤い円背景（点滅アニメーション） */}
            <circle
              cx={warningX}
              cy={warningY}
              r={hexSize * 0.5}
              fill="hsl(var(--destructive))"
              style={{
                animation: 'pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
            {/* ビックリマーク */}
            <text
              x={warningX}
              y={warningY + hexSize * 0.3}
              textAnchor="middle"
              className="fill-white font-bold"
              style={{ fontSize: `${hexSize * 0.8}px` }}
            >
              !
            </text>
            {/* ラベル（下に表示） */}
            <text
              x={warningX}
              y={warningY + hexSize * 1.2}
              textAnchor="middle"
              className="fill-destructive font-bold"
              style={{ fontSize: `${hexSize * 0.8}px` }}
            >
              {labelText}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * 輸送済みキューブ数オーバーレイ（HTMLレイヤー）
 * MapDisplay の外側、左上に固定表示
 */
export function SeattleDeliveredCubesOverlay({
  deliveredCubes,
}: {
  deliveredCubes: number;
}) {
  const t = useTranslations();
  return (
    <div className="absolute top-1 left-1 md:top-2 md:left-2 z-10 bg-card/90 border border-border rounded px-2 py-1 md:px-3 md:py-1.5 shadow-sm">
      <div className="flex items-center gap-1.5">
        <span className="text-sm md:text-base">{t.gameEnd.seattle?.delivered}:</span>
        <span className="text-lg md:text-xl font-bold text-primary">{deliveredCubes}</span>
      </div>
    </div>
  );
}
